import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/v2/Toast";
import { useCreateWorkspace } from "@/lib/workspaces";

/**
 * Modal launched from `SidebarWorkspaceMenu`. Creates a second (or
 * Nth) workspace owned by the currently-signed-in operator, then
 * routes them to the onboarding wizard for the new tenant —
 * `/onboarding/welcome` skips itself for users with a `firstName`
 * already set, so the wizard starts at Workspace step (defaults
 * already populated by the backend) and the operator can just
 * click through Continue → Continue.
 */
export function CreateWorkspaceDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState("");
  const create = useCreateWorkspace();
  const toast = useToast();
  const navigate = useNavigate();

  const trimmed = name.trim();
  const disabled = trimmed.length === 0 || create.isPending;

  const onSubmit = async () => {
    if (disabled) return;
    try {
      await create.mutateAsync({ workspaceName: trimmed });
      toast.success(
        "Workspace created",
        "Let's finish setting it up.",
      );
      onOpenChange(false);
      setName("");
      // The gate redirects authenticated operators with a name set
      // straight to /onboarding/workspace (skipping Welcome and
      // Personal). Navigate to "/" and let the gate route correctly
      // — avoids a flash through the skipped pages.
      navigate("/", { replace: true });
    } catch (e) {
      const msg = (e as Error).message ?? "create_failed";
      const friendly = msg.includes("workspace_slug_taken")
        ? "That workspace name is already taken. Try another."
        : msg.includes("workspace_name_required")
          ? "Workspace name is required."
          : "Couldn't create the workspace. Try again.";
      toast.error("Couldn't create workspace", friendly);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Create a new workspace</DialogTitle>
          <DialogDescription>
            Run a second business under the same account. Bookings,
            services, and clients are isolated per workspace — only
            your name and email are shared.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-1.5 py-2">
          <Label htmlFor="ws-name" className="text-[13px] font-semibold">
            Workspace name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="ws-name"
            placeholder="Acme Photography"
            autoFocus
            autoComplete="organization"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !disabled) onSubmit();
            }}
          />
          <p className="text-xs text-muted-foreground">
            Currency and timezone default to your current workspace —
            you can adjust them in the next step.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={disabled} className="gap-2">
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {create.isPending ? "Creating…" : "Create workspace"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
